/**
 * @fileoverview MonthlyAllocations - Budget allocation tracking component.
 *
 * Displays budget allocation progress for the current month, showing how much
 * of each category's target budget has been allocated from template-based income
 * transactions.
 *
 * @description This component provides:
 * - Category-by-category allocation breakdown
 * - Target amount from budget template
 * - Over Target amount (allocation exceeding target)
 * - Total allocated amount
 * - Remaining budget until target
 * - Visual progress bar
 * - Summary totals (Target Budget, Allocated, Remaining)
 *
 * Display format per category:
 * - Target: Budget template target amount
 * - Over Target: max(0, Allocated - Target)
 * - Allocated: Total amount allocated to category
 * - Remaining: Target - Allocated (can be negative)
 * - Progress bar: Visual representation of allocation percentage
 *
 * @module components/BudgetTemplate/MonthlyAllocations
 */
import { useState } from 'react';
import { formatCurrency } from '../../utils/moneyUtils';
import styles from './MonthlyAllocations.module.css';

/**
 * MonthlyAllocations Component
 *
 * Displays budget allocation progress for the current month.
 * Shows how much of each budget category has been allocated through template income.
 * Only visible when viewing the current month and when allocations exist.
 *
 * @param {Object} props - Component props
 * @param {Object} props.monthlyAllocations - Allocation data keyed by category name
 * @param {number} props.monthlyAllocations[category].target - Target budget amount
 * @param {number} props.monthlyAllocations[category].allocated - Amount allocated
 * @param {number} props.monthlyAllocations[category].remaining - Amount remaining
 * @param {Object} props.budgetTemplate - Budget template with priority group info
 * @param {boolean} props.isCurrentMonth - Whether currently viewing current month
 * @param {boolean} [props.alwaysExpanded=false] - If true, always show without toggle
 */
function MonthlyAllocations({ monthlyAllocations, budgetTemplate, isCurrentMonth, alwaysExpanded = false }) {
    const [showAllocations, setShowAllocations] = useState(alwaysExpanded);

    // Don't show if no allocations
    if (!monthlyAllocations) {
        return null;
    }

    // Calculate totals for the summary
    const totalTargetBudget = Object.values(monthlyAllocations).reduce((sum, cat) => sum + cat.target, 0);
    const totalAllocated = Object.values(monthlyAllocations).reduce((sum, cat) => sum + cat.allocated, 0);
    const totalRemaining = Object.values(monthlyAllocations).reduce((sum, cat) => sum + cat.remaining, 0);

    return (
        <div className={styles.container}>
            {!alwaysExpanded && (
                <div className={styles.toggleHeader}>
                    <h3 className={styles.title}>Monthly Budget Allocations</h3>
                    <button
                        onClick={() => setShowAllocations(!showAllocations)}
                        className={styles.toggleButton}
                    >
                        {showAllocations ? 'Hide Allocations' : 'Show Allocations'}
                    </button>
                </div>
            )}

            {showAllocations && (
                <>
                    {/* Summary Bar */}
                    <div className={styles.summaryBar}>
                        <strong className={styles.summaryLabel}>Template Status:</strong>
                        <span className={styles.summaryValue}>
                            Target Budget: {formatCurrency(totalTargetBudget)}
                        </span>
                        <span className={styles.summarySeparator}>|</span>
                        <span className={styles.summaryValue}>
                            Allocated: {formatCurrency(totalAllocated)}
                        </span>
                        <span className={styles.summarySeparator}>|</span>
                        <span className={styles.summaryValue}>
                            Remaining: {formatCurrency(totalRemaining)}
                        </span>
                    </div>

                    {/* Allocation Grid */}
                    <div className={styles.allocationGrid}>
                        {Object.entries(monthlyAllocations).map(([category, data]) => {
                            // Calculate progress percentage
                            const progressPercentage = data.target > 0 ? Math.min(100, (data.allocated / data.target) * 100) : 0;
                            const isComplete = data.allocated >= data.target;
                            const overTarget = Math.max(0, data.allocated - data.target);

                            // Get priority group from budget template
                            const priorityGroup = budgetTemplate?.[category]?.priorityGroup || 1;

                            return (
                                <div key={category} className={styles.allocationCard}>
                                    {/* Category Name */}
                                    <div className={styles.categoryName}>
                                        <span className={styles.categoryNameText}>{category}</span>
                                        <span className={styles.priorityGroupText}>Group {priorityGroup}</span>
                                    </div>

                                    {/* Allocation Details */}
                                    <div className={styles.allocationDetails}>
                                        <div className={styles.detailRow}>Target: {formatCurrency(data.target)}</div>
                                        <div className={styles.detailRow}>Over Target: {formatCurrency(overTarget)}</div>
                                        <div className={styles.detailRow}>Allocated: {formatCurrency(data.allocated)}</div>
                                        <div className={`${styles.detailRow} ${styles.remainingAmount} ${data.remaining > 0 ? styles.remainingPositive : styles.remainingZero}`}>
                                            Remaining: {formatCurrency(data.remaining)}
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className={styles.progressBarContainer}>
                                        <div
                                            className={`${styles.progressBar} ${isComplete ? styles.progressBarComplete : styles.progressBarPartial}`}
                                            style={{ width: `${progressPercentage}%` }}
                                        ></div>
                                    </div>

                                    {/* Progress Percentage */}
                                    <div className={styles.progressPercentage}>
                                        {progressPercentage.toFixed(1)}% allocated
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

export default MonthlyAllocations;